// Whose auth signs a partner's shoppers in — the ops screen's half of it.
//
// Sibling of partner-products-state.ts, and here for the same reason: two
// fields whose ILLEGAL COMBINATION is what the database refuses, so a form
// that validates each field on its own looks fine right up to the 422.
//
//   auth_provider = 'partner' with no auth_introspection_url is refused by
//   0219's CHECK partners_auth_introspection_url_present, and reaches the
//   admin as `introspection_url_required` — after a Save that looked correct.
//
// The API's resolveAuthProviderUpdate (apps/api/src/modules/partners/
// service.ts) stays the authority and the backstop; it judges the resulting
// ROW, including patches this UI cannot produce. This is the guard rail in
// front of it, not a second implementation of it. The SSRF check on the URL
// is deliberately NOT mirrored here — it resolves DNS, which a browser cannot
// do, and a half-copy of it would be the half that lets 169.254.169.254
// through.

import type { PartnerAuthProvider } from '@drivecars/shared';

export interface AuthState {
  auth_provider: PartnerAuthProvider;
  auth_introspection_url: string | null;
}

/** Why Save is refused, or null when the pair is legal. Rendered next to a
 *  disabled Save rather than thrown at ops as a toast after the round trip —
 *  the Products fieldset above set that standard. */
export function authSaveBlockReason(state: AuthState): string | null {
  if (state.auth_provider !== 'partner') return null;
  // `.trim()`, matching resolveAuthProviderUpdate: an empty string is not a
  // URL, and the CHECK only tests IS NOT NULL, so whitespace would save
  // cleanly into a row whose SDK still cannot mint a session.
  if ((state.auth_introspection_url ?? '').trim() === '') {
    return 'Partner auth needs an introspection URL — we have nowhere to verify their users’ tokens.';
  }
  return null;
}

/** The state transition for the provider select.
 *
 *  Switching AWAY from 'partner' keeps the URL rather than clearing it: ops
 *  flipping back and forth while reading a runbook should not lose a URL they
 *  already pasted, and a URL on a 'drivecars' row is inert — 0219's CHECK only
 *  constrains the 'partner' direction. Clearing it is an explicit act (the
 *  field is still editable), not a side effect of a select. */
export function nextAuthState(
  current: AuthState,
  action: { provider: PartnerAuthProvider },
): AuthState {
  if (action.provider === current.auth_provider) return current;
  return { ...current, auth_provider: action.provider };
}

/** How the secret field's three positions turn into what goes on the wire.
 *
 *  This is the whole reason the section needs more than a password input. The
 *  API treats the three cases differently and only two of them are typeable:
 *
 *    undefined  leave the stored secret exactly as it is
 *    null       clear it
 *    string     replace it
 *
 *  An empty password box MUST mean `undefined`. If it meant `null`, an ops
 *  user editing a partner's commission — never touching this section — would
 *  save a payload that silently wipes the bearer we present to that partner's
 *  introspection endpoint, and their sign-in would start failing hours later
 *  with nothing in the audit trail pointing here. Clearing is therefore its
 *  own control, and `intent` is what the control sets.
 */
export type SecretIntent =
  /** No opinion — the stored secret (if any) survives. The default, always. */
  | { kind: 'keep' }
  /** Ops pressed Replace and typed something. */
  | { kind: 'replace'; value: string }
  /** Ops pressed Clear. */
  | { kind: 'clear' };

export function secretForPayload(intent: SecretIntent): string | null | undefined {
  switch (intent.kind) {
    case 'replace':
      // A Replace box left empty is not a clear — it is an unfinished edit,
      // and the safe reading of an unfinished edit is to change nothing.
      return intent.value === '' ? undefined : intent.value;
    case 'clear':
      return null;
    case 'keep':
      return undefined;
  }
}
